import { lazy, Suspense, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Menu, X, MessageCircle } from "lucide-react";
import "@/styles/lp1.css";

const BelowFold = lazy(() => import("./lp1/BelowFold"));

const WA = "https://wa.me/966535632482";
const NAV = [
  { id: "services", label: "الخدمات" },
  { id: "results", label: "نتائجنا" },
  { id: "docs", label: "وثائقنا" },
  { id: "location", label: "موقعنا" },
];

const HERO_WORDS_1 = ["نُطلق", "متاجر", "تبيع."];
const HERO_WORDS_2 = ["ونصنع", "علاماتٍ", "تكبر."];

export default function Lp1() {
  const [scrolled, setScrolled] = useState(false);
  const [mopen, setMopen] = useState(false);
  const [active, setActive] = useState<string>("");
  const [showWa, setShowWa] = useState(false);

  /* Navbar scroll state + floating WA visibility (single passive listener) */
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = window.scrollY;
        setScrolled(y > 24);
        setShowWa(y > window.innerHeight * 0.6);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Scroll-spy */
  useEffect(() => {
    const ids = NAV.map(n => n.id);
    const els = ids.map(id => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (!els.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setActive(e.target.id); });
    }, { rootMargin: "-40% 0px -55% 0px", threshold: 0 });
    els.forEach(e => io.observe(e));
    return () => io.disconnect();
  }, []);

  return (
    <div className="lp1" lang="ar" dir="rtl">
      <Helmet>
        <html lang="ar" dir="rtl" />
        <title>إتقان · وكالة التسويق الرقمي السعودية | نتائج موثّقة منذ ٢٠١٨</title>
        <meta name="description" content="إتقان وكالة سعودية متخصصة في بناء وإدارة المتاجر الإلكترونية والتسويق الرقمي. أكثر من ٤٬٧٠٠ مشروع ناجح بنتائج موثّقة." />
        <meta name="theme-color" content="#04070F" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link rel="preload" as="style" href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&family=Space+Grotesk:wght@500;600;700&display=swap" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800&family=Space+Grotesk:wght@500;600;700&display=swap" />
      </Helmet>

      <div className="lp1-progress" aria-hidden="true"><i /></div>

      {/* NAVBAR */}
      <header className={`lp1-nav ${scrolled ? "scrolled" : ""}`}>
        <div className="lp1-nav-inner">
          <a href="#top" aria-label="إتقان">
            <img src="https://itqan.agency/brand/itqan-logo.png" alt="إتقان" className="lp1-logo" width={120} height={34} fetchPriority="high" />
          </a>
          <nav className="lp1-nav-links" aria-label="الرئيسية">
            {NAV.map(n => (
              <a key={n.id} href={`#${n.id}`} className={active === n.id ? "active" : ""}>{n.label}</a>
            ))}
          </nav>
          <a className="lp1-btn lp1-cta-nav" href={WA} target="_blank" rel="noopener noreferrer">احجز استشارة</a>
          <button className="lp1-burger" aria-label="القائمة" aria-expanded={mopen} onClick={() => setMopen(true)}>
            <Menu size={20} />
          </button>
        </div>
      </header>

      {/* Mobile panel */}
      <div className={`lp1-mbackdrop ${mopen ? "show" : ""}`} onClick={() => setMopen(false)} />
      <aside className={`lp1-mpanel ${mopen ? "open" : ""}`} aria-hidden={!mopen}>
        <button className="lp1-burger" aria-label="إغلاق" style={{ position: "absolute", top: 18, left: 18 }} onClick={() => setMopen(false)}><X size={20} /></button>
        {NAV.map(n => (
          <a key={n.id} href={`#${n.id}`} onClick={() => setMopen(false)}>{n.label}</a>
        ))}
        <a className="lp1-btn" style={{ marginTop: 12 }} href={WA} target="_blank" rel="noopener noreferrer">احجز استشارة</a>
      </aside>

      {/* HERO */}
      <section id="top" className="lp1-hero">
        <div className="lp1-hero-aurora" aria-hidden="true">
          <span className="lp1-blob b1" />
          <span className="lp1-blob b2" />
          <span className="lp1-blob b3" />
        </div>
        <div className="lp1-grid-bg" aria-hidden="true" />
        <div className="lp1-wrap lp1-hero-inner">
          <div>
            <span className="lp1-eyebrow">وكالة التسويق الرقمي · الرياض</span>
            <h1>
              <span style={{ display: "block" }}>
                {HERO_WORDS_1.map((w, i) => (
                  <span key={i} className="word" style={{ ["--w" as never]: i, marginInlineEnd: 14 }}>{w}</span>
                ))}
              </span>
              <span style={{ display: "block" }} className="grad">
                {HERO_WORDS_2.map((w, i) => (
                  <span key={i} className="word" style={{ ["--w" as never]: i + HERO_WORDS_1.length, marginInlineEnd: 14 }}>{w}</span>
                ))}
              </span>
            </h1>
            <p className="lp1-hero-sub">
              منصة سعودية متخصصة في بناء وإدارة المتاجر الإلكترونية. أكثر من ٤٬٧٠٠ مشروع ناجح منذ ٢٠١٨ — أرقام موثّقة، لا وعود.
            </p>
            <div className="lp1-hero-ctas">
              <a className="lp1-btn" href={WA} target="_blank" rel="noopener noreferrer">احجز استشارة مجانية</a>
              <a className="lp1-btn-outline" href="#results">شاهد نتائجنا</a>
            </div>
            <div className="lp1-chips" aria-label="مؤشرات سريعة">
              <span className="lp1-chip">+٤٬٧٠٠ مشروع</span>
              <span className="lp1-chip">منذ ٢٠١٨</span>
              <span className="lp1-chip">٩ قطاعات سعودية</span>
            </div>
          </div>

          <div className="lp1-hero-vis" aria-hidden="true">
            <span className="ring" />
            <span className="tube t1" />
            <span className="tube t2" />
            <span className="tube t3" />
            <div className="num">٤٫٧K<span>PROJECTS · KSA</span></div>
          </div>
        </div>
      </section>

      {/* Below the fold (lazy) */}
      <Suspense fallback={<div style={{ minHeight: 600 }} />}>
        <BelowFold />
      </Suspense>

      {/* Floating WhatsApp */}
      <a
        href={WA}
        target="_blank"
        rel="noopener noreferrer"
        className={`lp1-wa ${showWa ? "show" : ""}`}
        aria-label="تواصل عبر واتساب"
      >
        <MessageCircle size={26} strokeWidth={2.2} />
      </a>
    </div>
  );
}
